import { useRef, useEffect, useCallback, useMemo } from "react";
import { formatTime } from "@/lib/audio";

export interface TranscriptWord {
  word: string;
  start: number;
  end: number;
  confidence: number | null;
}

export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  words: TranscriptWord[];
}

interface TranscriptViewerProps {
  segments: TranscriptSegment[];
  currentTime: number;
  onSeek: (time: number) => void;
  isPlaying?: boolean;
}

const getConfidenceColor = (confidence: number | null): string => {
  if (confidence === null) return "text-foreground";
  if (confidence >= 0.9) return "text-green-600 dark:text-green-400";
  if (confidence >= 0.7) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
};

const getConfidenceOpacity = (confidence: number | null): number => {
  if (confidence === null) return 1;
  return Math.max(0.5, confidence);
};

export default function TranscriptViewer({
  segments,
  currentTime,
  onSeek,
  isPlaying = false,
}: TranscriptViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeWordRef = useRef<HTMLSpanElement | null>(null);
  const lastScrolledSegment = useRef<number>(-1);

  // Index of the segment currently being spoken, so auto-scroll fires only when
  // it changes — not on every timeupdate tick (which fought manual scrolling).
  const activeSegmentIndex = useMemo(
    () =>
      segments.findIndex(
        (segment) => currentTime >= segment.start && currentTime < segment.end
      ),
    [segments, currentTime]
  );

  const isWordActive = useCallback(
    (word: TranscriptWord): boolean => {
      return currentTime >= word.start && currentTime < word.end;
    },
    [currentTime]
  );

  const isSegmentActive = useCallback(
    (segment: TranscriptSegment): boolean => {
      return currentTime >= segment.start && currentTime < segment.end;
    },
    [currentTime]
  );

  useEffect(() => {
    if (activeSegmentIndex === -1) return;
    if (activeSegmentIndex === lastScrolledSegment.current) return;
    lastScrolledSegment.current = activeSegmentIndex;
    if (activeWordRef.current && containerRef.current) {
      activeWordRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [activeSegmentIndex]);

  const handleWordClick = (word: TranscriptWord) => {
    onSeek(word.start);
  };

  if (!segments || segments.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No transcript available
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="max-h-96 overflow-y-auto p-4 bg-card rounded-lg border"
    >
      <div className="space-y-4">
        {segments.map((segment, segIndex) => (
          <div
            key={segIndex}
            className={`transition-opacity duration-200 ${
              isSegmentActive(segment) ? "opacity-100" : "opacity-60"
            }`}
          >
            <span className="text-xs text-muted-foreground font-mono mr-2">
              [{formatTime(segment.start)}]
            </span>
            <span className="text-sm leading-relaxed">
              {segment.words.map((word, wordIndex) => {
                const isActive = isWordActive(word);
                const confidenceColor = getConfidenceColor(word.confidence);
                const confidenceOpacity = getConfidenceOpacity(word.confidence);

                return (
                  <span
                    key={wordIndex}
                    ref={isActive ? activeWordRef : null}
                    onClick={() => handleWordClick(word)}
                    className={`inline cursor-pointer transition-all duration-150 rounded px-0.5 ${
                      isActive
                        ? "bg-primary/20 text-primary font-semibold scale-105"
                        : "hover:bg-muted"
                    }`}
                    style={{ opacity: confidenceOpacity }}
                    title={
                      word.confidence !== null
                        ? `Confidence: ${Math.round(word.confidence * 100)}%`
                        : undefined
                    }
                  >
                    <span className={confidenceColor}>{word.word}</span>
                    {wordIndex < segment.words.length - 1 && " "}
                  </span>
                );
              })}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            High confidence (90%+)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
            Medium (70-89%)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            Low (&lt;70%)
          </span>
        </div>
        <div>
          Click any word to jump to that position
        </div>
      </div>
    </div>
  );
}
